import React, { useState, useMemo } from 'react';
import {
  Box, 
  Typography,
} from '@mui/material';
import { GridColDef, GridInitialState, GridRenderCellParams, GridColType } from '@mui/x-data-grid-premium';
import GaneshDataGrid from '../DataGrid/GaneshDataGrid';
import useApi from '../../hooks/useGaneshApi';
import { useQuery } from '@tanstack/react-query';
import { useError } from '../../contexts/ErrorContext';
import { CustomerHealthCheck_T, CustomerHealthCheckDetail_T } from '../../types/Customer';
import { healthCheckRenderers, healthCheckToFieldMap } from '../../utils/healthCheckRenderers';
import { Link } from 'react-router-dom';
import { formatNumber } from '../../utils/customFormat';
import { getDateValue } from '../../utils/dataGridUtils';
import HealthCheckSummaryCards from './HealthCheckSummaryCards';

const CustomerHealthChecks: React.FC = () => {
  const { apiRequest } = useApi();
  const { showError } = useError();
  
  // State for filtering the customer issues table
  const [selectedFilter, setSelectedFilter] = useState<string>('all');

  // Fetch health check data (includes summary and all customers with issues)
  const fetchHealthCheckData = async () => {
    const response = await apiRequest<{
      summary: CustomerHealthCheck_T[];
      customers: CustomerHealthCheckDetail_T[];
    }>('/v1/customers/health', { method: 'GET' });
    return response.data;
  };

  // Health check data query
  const { 
    data: healthCheckData, 
    isLoading: healthChecksLoading, 
    isError: healthChecksError 
  } = useQuery({
    queryKey: ['customer-health'],
    queryFn: fetchHealthCheckData,
    staleTime: 120000, // 2 minutes
    refetchInterval: false,
  });

  // Show error message if query failed
  if (healthChecksError) {
    showError('Failed to load customer health checks');
  }

  // Extract summary and customers from response
  const healthChecks = healthCheckData?.summary || [];
  const allCustomersWithIssues = healthCheckData?.customers || [];

  // Handle card clicks to change filter
  const handleCardFilter = (filterType: string) => {
    setSelectedFilter(filterType);
  };

  // Filter customers based on selected filter (client-side filtering)
  const customerIssuesData = useMemo(() => {
    if (!allCustomersWithIssues.length) return [];
    
    switch (selectedFilter) {
      case 'all':
        return allCustomersWithIssues;
      case 'critical':
        return allCustomersWithIssues.filter(c => c.has_critical_issues);
      case 'urgent':
        return allCustomersWithIssues.filter(c => c.has_urgent_issues);
      default:
        return allCustomersWithIssues;
    }
  }, [allCustomersWithIssues, selectedFilter]);

  // Calculate summary row data based on health_issues from backend
  const summaryRow = useMemo(() => {
    if (!customerIssuesData?.length) return null;
    
    // Count how many customers have issues in each field
    const fieldIssueCounts: Record<string, Set<number>> = {};
    
    customerIssuesData.forEach(customer => {
      const healthIssues = customer.health_issues || [];
      
      healthIssues.forEach((checkName: string) => {
        const field = healthCheckToFieldMap[checkName];
        if (field) {
          if (!fieldIssueCounts[field]) {
            fieldIssueCounts[field] = new Set();
          }
          fieldIssueCounts[field].add(customer.id);
        }
      });
    });
    
    const summary = {
      id: 'SUMMARY',
      name: `TOTAL (${customerIssuesData.length} customers)`,
      customer_status: fieldIssueCounts['customer_status']?.size || 0,
      active_fg_count: fieldIssueCounts['active_fg_count']?.size || 0,
      delivery_data_format: fieldIssueCounts['delivery_data_format']?.size || 0,
      fulfillment_destination_type: fieldIssueCounts['fulfillment_destination_type']?.size || 0,
      most_recent_delivery: fieldIssueCounts['most_recent_delivery']?.size || 0,
      next_scheduled_delivery: fieldIssueCounts['next_scheduled_delivery']?.size || 0,
      next_license_end_date: fieldIssueCounts['next_license_end_date']?.size || 0,
    };
    
    return summary;
  }, [customerIssuesData]);

  // Add summary row to data
  const dataWithSummary = useMemo(() => {
    if (!summaryRow) return customerIssuesData || [];
    return [...(customerIssuesData || []), summaryRow];
  }, [customerIssuesData, summaryRow]);

  const getFilterTitle = () => {
    switch (selectedFilter) {
      case 'all': return 'All Customers with Issues';
      case 'critical': return 'Customers with Critical Issues';
      case 'urgent': return 'Customers with Urgent Issues';
      default: return `Customers with: ${selectedFilter}`;
    }
  };

  // Custom renderer that shows normal data for regular rows, count for summary row
  const summaryAwareRenderer = (normalRenderer: (params: GridRenderCellParams) => React.ReactNode) => (params: GridRenderCellParams) => {
    if (params.row.id === 'SUMMARY') {
      const count = params.value as number;
      return (
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: count > 0 ? 'error.main' : 'text.primary' }}>
          {formatNumber(count)}
        </Typography>
      );
    }
    return normalRenderer(params);
  };

  // Special renderer for date columns that handles both dates and summary counts
  const dateAwareSummaryRenderer = (params: GridRenderCellParams) => {
    if (params.row.id === 'SUMMARY') {
      const count = params.value as number;
      return (
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: count > 0 ? 'error.main' : 'text.primary' }}>
          {formatNumber(count)}
        </Typography>
      );
    }
    return healthCheckRenderers.dateWithMissingCheck(params);
  };

  // Customer issues table columns
  const customerIssuesColumns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.id === 'SUMMARY') {
          return <Typography variant="body2" sx={{ fontWeight: 'bold' }}>--</Typography>;
        }
        return <Link to={`/customer/${params.value}`}>{params.value}</Link>;
      }
    },
    {
      field: 'name',
      headerName: 'Customer Name',
      width: 200,
      flex: 1,
      renderCell: healthCheckRenderers.stringWithMissingCheck
    },
    {
      field: 'customer_status',
      headerName: 'Status',
      width: 100,
      description: 'Missing Status: Checks if customer status is missing or empty',
      renderCell: summaryAwareRenderer(healthCheckRenderers.stringWithMissingCheck)
    },
    {
      field: 'active_delivery_count',
      headerName: 'Active Deliveries',
      width: 130,
      type: 'number',
      renderCell: summaryAwareRenderer(healthCheckRenderers.numberWithMissingCheck)
    },
    {
      field: 'active_fg_count',
      headerName: 'Active FGs',
      width: 100,
      type: 'number',
      description: 'Missing Fulfillment Group: Checks if Current customers have no active fulfillment groups',
      renderCell: summaryAwareRenderer(healthCheckRenderers.countWithZeroIssue)
    },
    {
      field: 'delivery_data_format',
      headerName: 'Data Format',
      width: 120,
      description: 'Missing Data Format: Checks if Current customers are missing delivery data format',
      renderCell: summaryAwareRenderer(healthCheckRenderers.stringWithMissingCheck)
    },
    {
      field: 'fulfillment_destination_type',
      headerName: 'Destination',
      width: 120,
      description: 'Missing Fulfillment Destination: Checks if Current customers are missing fulfillment destination',
      renderCell: summaryAwareRenderer(healthCheckRenderers.stringWithMissingCheck)
    },
    {
      field: 'most_recent_delivery',
      headerName: 'Last Delivery',
      width: 130,
      type: 'date' as GridColType,
      valueGetter: getDateValue,
      description: 'Missing/Invalid Recent Delivery: Checks if Current customers have missing or future delivery dates',
      renderCell: dateAwareSummaryRenderer
    },
    {
      field: 'next_scheduled_delivery',
      headerName: 'Next Delivery',
      width: 130,
      type: 'date' as GridColType,
      valueGetter: getDateValue,
      description: 'Missing/Invalid Next Scheduled Delivery: Checks if Current customers have missing or past scheduled deliveries',
      renderCell: dateAwareSummaryRenderer
    },
    {
      field: 'next_license_end_date',
      headerName: 'License Ends',
      width: 130,
      type: 'date' as GridColType,
      valueGetter: getDateValue,
      description: 'Missing/Invalid License End Date: Checks if Current customers have missing or expired licenses',
      renderCell: dateAwareSummaryRenderer
    },
  ];

  const customerIssuesGridState: GridInitialState = {
    sorting: { sortModel: [{ field: 'id', sort: 'asc' }] },
    pagination: { paginationModel: { pageSize: 25, page: 0 } },
    density: "compact",
  };
  
  return (
    <Box sx={{ px: 1, py: 0 }}>
      {/* 1. Summary Cards */}
      <HealthCheckSummaryCards 
        healthChecks={healthChecks ?? []} 
        loading={healthChecksLoading}
        onCardClick={handleCardFilter}
        selectedFilter={selectedFilter}
      />
      
      {/* 2. Customer Issues Table with Summary Row */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {getFilterTitle()}
        </Typography>
        
        <GaneshDataGrid
          gridId="customerIssuesTable"
          data={dataWithSummary}
          columns={customerIssuesColumns}
          loading={healthChecksLoading}
          defaultGridState={customerIssuesGridState}
          sx={{
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            },
            // Style the summary row differently
            "& .MuiDataGrid-row[data-id='SUMMARY']": {
              backgroundColor: 'action.hover',
              fontWeight: 'bold',
              borderTop: '2px solid',
              borderTopColor: 'divider',
              "& .MuiDataGrid-cell": {
                fontWeight: 'bold',
                color: 'primary.main',
              }
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default CustomerHealthChecks;
